import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { adminApi } from '@/lib/adminApi';
import { feedbackApi } from '@/lib/feedbackApi';
import { panelList } from '@/lib/panels';
import { diffAliasPatterns } from '@/lib/biomarkers';
import { useAuth } from '@/providers/AuthProvider';
import { roleSatisfies } from '@/lib/roleRank';
import { DomainPill } from '@/components/DomainPill';
import { Button } from '@/components/ui/button';
import { AdminPage, EntityFormModal, FeedbackControl, type FieldSpec } from '@/admin';

const ALIAS_FIELDS: FieldSpec[] = [
  { name: 'patterns', label: 'Alias patterns (one per line)', type: 'lines', optional: true,
    hint: 'Each pattern points at this biomarker (PUT overwrites any existing mapping). Removing a line deletes that alias. Patterns are lowercased.' },
];

function Meta({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-text-secondary">{label}</div>
      <div className="mt-1 text-body-sm">{children}</div>
    </div>
  );
}

export function BiomarkerDetailPage() {
  const { name = '' } = useParams();
  const qc = useQueryClient();
  const { role } = useAuth();
  const isAdmin = roleSatisfies(role ?? '', 'admin');
  const bq = useQuery({ queryKey: ['biomarkers'], queryFn: () => adminApi.biomarkers.list() });
  const pq = useQuery({ queryKey: ['panels'], queryFn: () => adminApi.panels.list() });
  const fb = useQuery({ queryKey: ['feedback-summary', 'biomarkers'], queryFn: () => feedbackApi.summary('biomarkers') });

  const b = bq.data?.biomarkers.find((x) => x.name === name);
  const inPanels = panelList(pq.data).filter((p) => (p.biomarkers ?? []).includes(name));
  const refresh = () => void qc.invalidateQueries({ queryKey: ['feedback-summary', 'biomarkers'] });

  const [editing, setEditing] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const currentPatterns = (b?.aliases ?? []).map((a) => a.pattern);

  const save = useMutation({
    mutationFn: async (patterns: string[]) => {
      const { toAdd, toRemove } = diffAliasPatterns(currentPatterns, patterns);
      const errors: string[] = [];
      for (const p of toAdd) {
        try { await adminApi.biomarkers.upsertAlias(p, name); }
        catch (e) { errors.push(`add "${p}": ${e instanceof Error ? e.message : 'failed'}`); }
      }
      for (const p of toRemove) {
        try { await adminApi.biomarkers.deleteAlias(p); }
        catch (e) {
          const m = e instanceof Error ? e.message : 'failed';
          if (!/no alias/i.test(m)) errors.push(`remove "${p}": ${m}`); // 404 = already gone, tolerate
        }
      }
      if (errors.length) throw new Error(errors.join('\n'));
    },
    onSuccess: () => { setErr(null); setEditing(false); void qc.invalidateQueries({ queryKey: ['biomarkers'] }); },
    onError: (e) => setErr(e instanceof Error ? e.message : 'Save failed'),
  });

  return (
    <AdminPage
      title={name}
      mono
      backTo={{ to: '/admin/biomarkers', label: 'All biomarkers' }}
    >
      {bq.isLoading ? <p className="text-body-sm text-text-muted">Loading…</p> : null}
      {bq.data && !b ? <p className="text-body-sm text-flagged">No biomarker “{name}”.</p> : null}

      {b ? (
        <>
          <div className="mb-21 flex items-center gap-8">
            {b.primary_domain ? <DomainPill domain={b.primary_domain} /> : null}
            <FeedbackControl registry="biomarkers" itemId={name} itemLabel={name} openCount={fb.data?.[name]?.open ?? 0} onChanged={refresh} />
          </div>

          <div className="mb-21 grid grid-cols-2 gap-13 rounded border border-border bg-surface p-21 md:grid-cols-4">
            <Meta label="Primary domain">{b.primary_domain
              ? <Link to={`/admin/domains/${encodeURIComponent(b.primary_domain)}`} className="text-accent hover:underline">{b.primary_domain}</Link>
              : '—'}</Meta>
            <Meta label="Consumer domains">{(b.consumer_domains ?? []).join(', ') || '—'}</Meta>
            <Meta label="Data source">{b.data_source || '—'}</Meta>
            <Meta label="Recency">{b.recency_days ? `${b.recency_days} days` : '—'}</Meta>
            <Meta label="Measurable">{b.measurable ? 'yes' : 'no'}</Meta>
            <Meta label="Formulation relevant">{b.formulation_relevant ? 'yes' : 'no'}</Meta>
          </div>

          <section className="mb-21">
            <h2 className="mb-8 text-h3 font-semibold">In panels ({inPanels.length})</h2>
            {inPanels.length ? (
              <div className="flex flex-wrap gap-2">
                {inPanels.map((p) => (
                  <Link key={p.panel_id} to={`/admin/panels/${encodeURIComponent(p.panel_id)}`}
                    className="rounded-pill border border-border px-2 py-0.5 text-[12px] text-accent hover:bg-surface-sunken">{p.label || p.panel_id}</Link>
                ))}
              </div>
            ) : <p className="text-body-sm text-text-muted">Not referenced by any panel.</p>}
          </section>

          <section>
            <div className="mb-8 flex items-center justify-between gap-8">
              <h2 className="text-h3 font-semibold">Aliases ({(b.aliases ?? []).length})</h2>
              {isAdmin ? (
                <Button variant="secondary" onClick={() => { setErr(null); setEditing(true); }}>Edit aliases</Button>
              ) : null}
            </div>
            {(b.aliases ?? []).length ? (
              <div className="overflow-x-auto rounded border border-border bg-surface">
                <table className="w-full border-collapse text-body-sm">
                  <thead>
                    <tr className="border-b border-border-strong text-left text-[12px] uppercase tracking-wide text-text-secondary">
                      <th className="p-2 font-semibold">Pattern</th><th className="p-2 font-semibold">Source</th><th className="p-2 font-semibold">Confidence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(b.aliases ?? []).map((a, i) => (
                      <tr key={`${a.pattern}-${i}`} className="border-b border-border">
                        <td className="p-2 font-mono text-[12px]">{a.pattern}</td>
                        <td className="p-2 text-text-secondary">{a.source}</td>
                        <td className="p-2 font-mono text-[12px]">{a.confidence}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <p className="text-body-sm text-text-muted">No aliases.</p>}
          </section>

          {editing ? (
            <EntityFormModal
              title={`Edit aliases — ${name}`}
              fields={ALIAS_FIELDS}
              initialValues={{ patterns: currentPatterns }}
              mode="edit"
              error={err}
              busy={save.isPending}
              onSubmit={(v) => save.mutate((v.patterns as string[]) ?? [])}
              onClose={() => { setEditing(false); setErr(null); }}
            />
          ) : null}
        </>
      ) : null}
    </AdminPage>
  );
}
